import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
} from "@/components/ui/card";
import ContentEditor from "@/modules/lcms/content-courses/components/content-editor";
import { Question } from "../../schemas/schemas";
import { Activity, BookContent } from "./types";

interface ActivityCardProps {
  activity: Activity;
  onActivityContentChange: (activityId: string, newContent: string) => void;
  onOpenQuestionBank: (activityId: string) => void;
  onEditQuestion: (question: Question) => void;
  onRemoveQuestion: (questionId: string) => void;
  onContentUpload?: (activityId: string, file: File, contentType: string) => void;
  onContentDelete?: (activityId: string, contentId: string) => void;
  t: (key: string) => string;
}

function getActivityIcon(activityType: string) {
  switch (activityType) {
    case "video":
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polygon points="23 7 16 12 23 17 23 7"></polygon>
          <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
        </svg>
      );
    case "pdf":
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
          <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
        </svg>
      );
    case "session":
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
          <line x1="16" y1="2" x2="16" y2="6"></line>
          <line x1="8" y1="2" x2="8" y2="6"></line>
          <line x1="3" y1="10" x2="21" y2="10"></line>
        </svg>
      );
    case "exam":
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14 2 14 8 20 8"></polyline>
          <line x1="16" y1="13" x2="8" y2="13"></line>
          <line x1="16" y1="17" x2="8" y2="17"></line>
        </svg>
      );
    default:
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10"></circle>
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
          <line x1="12" y1="17" x2="12.01" y2="17"></line>
        </svg>
      );
  }
}

function getActivityTypeStyle(activityType: string) {
  switch (activityType) {
    case 'session':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    case 'exam':
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
    case 'exercise':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    case 'lesson':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
  }
}

function getContentTypeIcon(contentType: string) {
  switch (contentType) {
    case 'scorm':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
          <rect x="7" y="8" width="10" height="8" rx="1" ry="1"></rect>
        </svg>
      );
    case 'pdf':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14 2 14 8 20 8"></polyline>
          <line x1="16" y1="13" x2="8" y2="13"></line>
          <line x1="16" y1="17" x2="8" y2="17"></line>
          <polyline points="10 9 9 9 8 9"></polyline>
        </svg>
      );
    case 'video':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="23 7 16 12 23 17 23 7"></polygon>
          <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
        </svg>
      );
    case 'epub':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
          <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
          <path d="M6 8h2"></path>
          <path d="M6 12h2"></path>
          <path d="M16 8h2"></path>
          <path d="M16 12h2"></path>
        </svg>
      );
    default:
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14 2 14 8 20 8"></polyline>
        </svg>
      );
  }
}

function getContentTypeColor(contentType: string) {
  switch (contentType) {
    case 'scorm':
      return 'bg-purple-100 text-purple-800 border-purple-200';
    case 'pdf':
      return 'bg-red-100 text-red-800 border-red-200';
    case 'video':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'epub':
      return 'bg-green-100 text-green-800 border-green-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
}

function getAcceptedFileTypes(contentType: string) {
  switch (contentType) {
    case 'scorm':
      return '.zip';
    case 'pdf':
      return '.pdf';
    case 'video':
      return '.mp4,.mov,.avi,.wmv,.flv,.webm';
    case 'epub':
      return '.epub';
    default:
      return '';
  }
}

function formatFileSize(bytes: number) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export function ActivityCard({
  activity,
  onActivityContentChange,
  onOpenQuestionBank,
  onEditQuestion,
  onRemoveQuestion,
  onContentUpload,
  onContentDelete,
  t,
}: ActivityCardProps) {
  const [selectedContentType, setSelectedContentType] = useState<string>('pdf');
  const [isUploading, setIsUploading] = useState(false);

  const contentTypes = [
    { value: 'scorm', label: 'SCORM Package' },
    { value: 'pdf', label: 'PDF Document' },
    { value: 'video', label: 'Video File' },
    { value: 'epub', label: 'EPUB Book' }
  ];

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !onContentUpload) return;

    setIsUploading(true);
    try {
      await onContentUpload(activity.activity_id, file, selectedContentType);
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setIsUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const handleDeleteContent = (contentId: string) => {
    if (onContentDelete) {
      onContentDelete(activity.activity_id, contentId);
    }
  };
  return (
    <Card
      key={activity.activity_id}
      className="border-0 border-t rounded-none border-gray-200 dark:border-gray-700 p-0 ps-0 pe-0 ml-4"
    >
      <CardHeader className="py-3 px-4 bg-gray-50 dark:bg-gray-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <span className="mr-2 text-gray-500">
              {getActivityIcon(activity.type)}
            </span>
            <div>
              <div className="font-medium">
                {activity.title}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {activity.duration && `${activity.duration} min • `}
                {activity.requiredForCompletion ? "Required" : "Optional"}
              </div>
            </div>
            <span
              className={`ml-2 inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getActivityTypeStyle(activity.type)}`}
            >
              {activity.type}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {(activity.type === "exercise" || activity.type === "exam") && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onOpenQuestionBank(activity.activity_id)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="mr-2"
                >
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                {t('course-content.composer.addQuestion')}
              </Button>
            )}
            <Button variant="ghost" size="sm">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                <polyline points="15 3 21 3 21 9"></polyline>
                <line x1="10" y1="14" x2="21" y2="3"></line>
              </svg>
            </Button>
            <Button variant="ghost" size="sm">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 20h9"></path>
                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
              </svg>
            </Button>
            <Button variant="ghost" size="sm">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"></path>
                <line x1="10" y1="11" x2="10" y2="17"></line>
                <line x1="14" y1="11" x2="14" y2="17"></line>
              </svg>
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-2">
        {activity.type === "exercise" || activity.type === "exam" ? (
          <div className="space-y-4">
            <div>
              <div className="mt-4 space-y-4">
                {(activity.questions || []).map((question: Question, index: number) => {
                  return (
                    <div key={question.question_id} className="border dark:border-gray-700 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div className="font-medium text-gray-800 dark:text-gray-200">
                          {index + 1}. {question.question_text}
                        </div>
                        <div className="flex space-x-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => onEditQuestion(question)}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
                            </svg>
                            <span className="sr-only">{t('course-content.composer.edit')}</span>
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => onRemoveQuestion(question.question_id)}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6"></polyline>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                              <line x1="10" y1="11" x2="10" y2="17"></line>
                              <line x1="14" y1="11" x2="14" y2="17"></line>
                            </svg>
                            <span className="sr-only">{t('course-content.composer.delete')}</span>
                          </Button>
                        </div>
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {question.question_type === 'multiple_choice' && question.options &&
                          question.options.map((option: string, idx: number) => (
                            <div key={idx} className="flex items-center">
                              <div className={`w-5 h-5 flex items-center justify-center mr-2 ${
                                question.correct_option === option 
                                  ? 'text-green-500' 
                                  : 'text-gray-400'
                              }`}>
                                {question.correct_option === option && (
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12"></polyline>
                                  </svg>
                                )}
                              </div>
                              {option}
                            </div>
                          ))
                        }
                      </div>
                    </div>
                  );
                })}
                
                {(!activity.questions || !activity.questions.length) && (
                  <div className="text-center p-8 border border-dashed dark:border-gray-700 rounded-lg">
                    <p className="text-gray-500 dark:text-gray-400">
                      {activity.type === "exam"
                        ? "No exam questions yet. Click 'Add Question' to get started."
                        : t('course-content.composer.noQuestionsYet')
                      }
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : activity.type === "session" ? (
          <div className="space-y-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <p><strong>Session Topic:</strong> {activity.topic}</p>
              {activity.duration && <p><strong>Duration:</strong> {activity.duration} minutes</p>}
              {activity.description && <p><strong>Description:</strong> {activity.description}</p>}
              {activity.default_platform && <p><strong>Platform:</strong> {activity.default_platform}</p>}
              {activity.default_location && <p><strong>Location:</strong> {activity.default_location}</p>}
            </div>
            <div>
              <ContentEditor
                initialContent={
                  activity.content_data ||
                  `Session content for ${activity.title}. Add session notes, materials, and instructions here.`
                }
                onChange={(newContent) =>
                  onActivityContentChange(
                    activity.activity_id,
                    newContent
                  )
                }
              />
            </div>
          </div>
        ) : activity.type === "lesson" ? (
          <div className="space-y-4">
            {/* Content Type Selection */}
            <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Content Type:
              </label>
              <div className="flex gap-2">
                {contentTypes.map((type) => (
                  <button
                    key={type.value}
                    onClick={() => setSelectedContentType(type.value)}
                    className={`px-3 py-1 text-xs rounded-full transition-colors ${
                      selectedContentType === type.value
                        ? getContentTypeColor(type.value)
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Upload Area */}
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6">
              <div className="text-center">
                <div className="mb-4">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full mb-4">
                    {getContentTypeIcon(selectedContentType)}
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                    Upload {contentTypes.find(t => t.value === selectedContentType)?.label}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                    Drag and drop your file here, or click to browse
                  </p>
                </div>
                
                <div className="flex items-center justify-center">
                  <label className={`cursor-pointer inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                    isUploading ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'
                  } transition-colors`}>
                    {isUploading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        Choose File
                      </>
                    )}
                    <input
                      type="file"
                      className="hidden"
                      accept={getAcceptedFileTypes(selectedContentType)}
                      onChange={handleFileUpload}
                      disabled={isUploading}
                    />
                  </label>
                </div>
                
                <p className="text-xs text-gray-400 mt-2">
                  Supported formats: {getAcceptedFileTypes(selectedContentType) || 'Various formats'}
                </p>
              </div>
            </div>

            {/* Uploaded Content List */}
            {activity.contents && activity.contents.length > 0 && (
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">Uploaded Content</h4>
                {activity.contents.map((content: BookContent) => (
                  <div key={content.id} className={`p-4 border rounded-lg ${getContentTypeColor(content.type)}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                          {getContentTypeIcon(content.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {content.file?.name || content.title}
                          </p>
                          <p className="text-xs text-gray-500">
                            {content.file?.size && formatFileSize(content.file.size)} • 
                            {content.type.toUpperCase()} • 
                            Status: {content.status || 'Ready'}
                          </p>
                          {content.description && (
                            <p className="text-xs text-gray-600 mt-1">{content.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {content.status === 'ready' && content.launch_url && (
                          <Button variant="ghost" size="sm">
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </Button>
                        )}
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDeleteContent(content.id)}
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {(!activity.contents || activity.contents.length === 0) && (
              <div className="text-center p-8 border border-dashed dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800">
                <div className="text-gray-400 mb-2">
                  {getContentTypeIcon(selectedContentType)}
                </div>
                <p className="text-gray-500 dark:text-gray-400 text-sm">
                  No content uploaded yet. Choose a content type and upload your first file.
                </p>
              </div>
            )}
          </div>
        ) : (
          <div>
            <ContentEditor
              initialContent={
                activity.content_data ||
                `Content for ${activity.title}.`
              }
              onChange={(newContent) =>
                onActivityContentChange(
                  activity.activity_id,
                  newContent
                )
              }
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
} 